import './_menu.scss'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react';

import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import * as spriteIcons from 'assets/svg/sprite'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { useModalContext } from 'context/modalContext'
import { usePageTitle } from 'context/pageTitleContext'
import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import { clearAllCachesForFan, clearAllCachesForModel } from 'helpers/clearAllCacheStores'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import { logoutUser } from 'services/endpoints/auth'
import {
  useAccessToken,
  useAuthStore,
  useUserProfile
} from 'store'
import { usePreviewStore } from 'store/previewStore'
import { useLocalStorageClear } from 'utils'

import MenuProfileHolder from './components/MenuProfileHolder'

export const Menu = () => {
    const [showMore, setShowMore] = useState<boolean>(false)

    const navigate = useNavigate()
    const userData = useUserProfile()!
    const modalData = useModalContext()
    const { t } = useTranslation()
    const location = useLocation()
    const queryClient = useQueryClient()
    const [isLoggingOut, setIsLoggingOut] = useState(false)
    const initialPath = useRef(location.pathname)

    // track latest path in a ref we can read during cleanup
    const latestPathRef = useRef(location.pathname)
    useEffect(() => {
        latestPathRef.current = location.pathname
    }, [location.pathname])

    const { title, setTitle } = usePageTitle()
    const prevTitleRef = useRef<ReactNode | null>(null)
    const currentTitleRef = useRef<ReactNode | null>(null)

    // CLOSE MENU IF ROUTE CHANGES
    useEffect(() => {
        if (location.pathname !== initialPath.current) {
        modalData.clearModal()
        }
    }, [location.pathname])

    // always track the latest title
    useEffect(() => {
        currentTitleRef.current = title
    }, [title])

    // temporary title with "only restore if same path" rule
    useEffect(() => {
        prevTitleRef.current = title
        setTitle('Menu')

        return () => {
        const navigatedAway = latestPathRef.current !== initialPath.current
        if (!navigatedAway && currentTitleRef.current === 'Menu' && prevTitleRef.current != null) {
            setTitle(prevTitleRef.current)
        }
        }
    }, [setTitle])
    

    const handleLogout = async () => {
    if (isLoggingOut) return

    const role =
        userData?.role === 'model'
        ? 'model'
        : 'fan'

    const token = useAccessToken()

    if (!token) {
        flushSync(() => {
        useAuthStore
            .getState()
            .setIsLoggingOut(true)
        })

        void queryClient.cancelQueries()

        usePreviewStore
        .getState()
        .resetPreviewState()

        queryClient.clear()

        if (role === 'model') {
        clearAllCachesForModel()
        } else {
        clearAllCachesForFan()
        }

        useAuthStore.setState({
        accessToken: '',
        userProfile: null
        })

        useLocalStorageClear()

        navigate('/', {
        replace: true
        })

        requestAnimationFrame(() => {
        useAuthStore
            .getState()
            .setIsLoggingOut(false)
        })

        return
    }

    try {
        setIsLoggingOut(true)

        const success = await logoutUser(token)

        if (!success) {
        return
        }

        flushSync(() => {
        useAuthStore
            .getState()
            .setIsLoggingOut(true)
        })

        void queryClient.cancelQueries()

        usePreviewStore
        .getState()
        .resetPreviewState()

        queryClient.clear()

        if (role === 'model') {
        clearAllCachesForModel()
        } else {
        clearAllCachesForFan()
        }

        useAuthStore.setState({
        accessToken: '',
        userProfile: null
        })

        useLocalStorageClear()

        navigate('/', {
        replace: true
        })

        requestAnimationFrame(() => {
        useAuthStore
            .getState()
            .setIsLoggingOut(false)
        })
    } finally {
        setIsLoggingOut(false)
    }
    }
    const handleNavigate = (targetPath: string) => {
    if (location.pathname === targetPath) {
        modalData.clearModal()
    } else {
        navigate(targetPath)
    }
    }

    const menuCards = [
        {
            icon: <spriteIcons.IconNoteCurvy />,
            body: t('following'),
            link: '/following',
            absfix: true
        },
        {
            icon: <spriteIcons.IconPlayBlueLarge />,
            body: t('reels'),
            link: '/discover/reels',
        },
        {
            icon: <spriteIcons.IconDollarCircleNoFill />,
            body: t('earnings'),
            link: '/earnings/sales',
            absfix: true
        },
        {
            icon: <spriteIcons.IconAnalytics />,
            body: t('analytics'),
            link: '/analytics/sales',
            absfix: true
        },
        {
            icon: <spriteIcons.IconCalendarLarge />,
            body: t('schedule'),
            link: '/schedule',
            absfix: true
        },
        {
            icon: <spriteIcons.IconThreePeople />,
            body: t('Fans'),
            link: '/fans',
            absfix: true
        },
        ...(userData?.overall_revenue > 200
        ? [
            {
                icon: <spriteIcons.IconPeopleHeart />,
                body: t('friends'),
                link: '/friends',
                absfix: true,
                // pendingNumber: data?.friend_requests
            }
        ]
        : []),
        {
            icon: <spriteIcons.IconPictureSymbol />,
            body: t('sharedMedia'),
            link: '/vault',
            absfix: true
        },
        {
            icon: <spriteIcons.IconMediaFolder2 />,
            body: t('mediaCategories'),
            link: '/media_categories',
            absfix: true
        },
        {
            icon: <spriteIcons.IconSearchBlue />,
            body: t('browseCreators'),
            link: '/creators',
            absfix: true
        },
        {
            icon: <spriteIcons.IconMediaFolder />,
            body: t('media'),
            link: '/media',
            absfix: true
        },
/*         {
            icon: <spriteIcons.IconLiveStream />,
            body: t('liveStreaming'),
            link: '/streams',
            absfix: true
        }, */
        {
            icon: <spriteIcons.IconTwoPeople />,
            body: 'Subscriptions',
            link: '/subscriptions',
            absfix: true
        },
        {
            icon: <spriteIcons.IconSettings />,
            body: t('settings'),
            link: '/settings/general',
            absfix: true
        },
        {
            icon: <spriteIcons.IconDollarCircleArrows />,
            body: t('transactions'),
            link: '/transactions',
            absfix: true
        },
        {
            icon: <spriteIcons.IconHowToBook />,
            body: t('howToTutorials'),
            link: '/how-to/tutorials',
            absfix: true
        },
        {
            icon: <spriteIcons.IconManHeadphones />,
            body: t('support'),
            link: '/support/home',
            absfix: true
        },
        {
        icon: <spriteIcons.IconHowToBook />,
        body: 'Terms & Policies',
        link: '/termsandpolicies',
        absfix: true,
        },
    ]

    const userMenuCards = [
        {
            icon: <spriteIcons.IconNoteCurvy />,
            body: t('feed'),
            link: '/',
            absfix: true
        },
        {
            icon: <spriteIcons.IconPlayBlueLarge />,
            body: t('reels'),
            link: '/discover/reels',
        },
        {
            icon: <spriteIcons.IconMediaFolder2 />,
            body: t('media'),
            link: '/media',
            absfix: true
        },
        {
            icon: <spriteIcons.IconTwoPeople />,
            body: t('subscriptions'),
            link: '/subscriptions',
            absfix: true
        },
        {
            icon: <spriteIcons.IconSearchBlue />,
            body: t('browseCreators'),
            link: '/creators',
            absfix: true
        },
        {
            icon: <spriteIcons.IconDollarCircleArrows />,
            body: t('transactions'),
            link: '/transactions',
            absfix: true
        },
        {
            icon: <spriteIcons.IconSettings />,
            body: t('settings'),
            link: '/settings/general',
            absfix: true
        },
        {
            icon: <spriteIcons.IconCreditCards />,
            body: (
                <span>
                    {t('yourCards')}{' '}
                    <span className='menu-option-subtxt'>
                        ({t('toSubscribe')})
                    </span>
                </span>
            ),
            link: '/settings/general/wallet',
            absfix: true
        },
        {
            icon: <spriteIcons.IconBecomeCreator />,
            body: t('becomeACreator'),
            absfix: true,
            link: '/become-a-creator'
        },
        {
            icon: <spriteIcons.IconHowToBook />,
            body: t('howToFaq'),
            link: '/how-to/tutorials',
            absfix: true
        },
        {
            icon: <spriteIcons.IconManHeadphones />,
            body: t('support'),
            link: '/support/home',
            absfix: true
        },
        {
        icon: <spriteIcons.IconHowToBook />,
        body: 'Terms & Policies',
        link: '/termsandpolicies',
        absfix: true,
        },
    ]

    return (
        <div className='menu__content__wrapper'>
            <WithHeaderSection
                headerSection={
                    <MenuProfileHolder
                        id={userData?.id}
                        profileImg={userData?.avatar?.url || ''}
                        display_name={userData?.display_name}
                        role={userData?.role}
                        follower_count={userData?.follower_count}
                        friend_count={userData?.friend_count}
                        following_count={userData.follower_count || 0}
                        wallet_deposit={userData?.wallet_deposit || 0}
                        username={userData?.username}
                    />
                }
                customClass='menu__header'
            >
                {userData?.role === 'model' ? (
                    <>
                        {menuCards.map(
                            (
                                card: {
                                    icon: string | JSX.Element | JSX.Element[]
                                    body: string | JSX.Element | JSX.Element[]
                                    absfix?: boolean
                                    link: string
                                    pendingNumber?: number
                                },
                                key: number
                            ) =>
                                key < 11 ? (
                                    <ActionCard
                                        icon={card.icon}
                                        iconSize='small'
                                        text={card.body}
                                        absFix={card.absfix}
                                        clickFn={() => handleNavigate(card.link)} // closes menu if already on page
                                        key={key}
                                        hasArrow
                                        pendingNumber={card.pendingNumber}
                                    />
                                ) : (
                                    showMore &&
                                    key >= 11 && (
                                        <ActionCard
                                            icon={card.icon}
                                            iconSize='small'
                                            text={card.body}
                                            absFix={card.absfix}
                                            clickFn={() => handleNavigate(card.link)}  // closes menu if already on page
                                            key={key}
                                            hasArrow
                                            pendingNumber={card.pendingNumber}
                                        />
                                    )
                                )
                        )}
                        {!showMore && (
                            <ActionCard
                                icon={<spriteIcons.IconThreeDotsCircle />}
                                iconSize='small'
                                text={t('more')}
                                clickFn={() => {
                                    setShowMore(true)
                                }}
                            />
                        )}
                    </>
                ) : (
                    userMenuCards.map(
                        (
                            card: {
                                icon: string | JSX.Element | JSX.Element[]
                                body: string | JSX.Element | JSX.Element[]
                                absfix?: boolean
                                link?: string
                                fn?: () => void
                            },
                            key: number
                        ) => {
                            if (card.link) {
                                return (
                                    <ActionCard
                                        icon={card.icon}
                                        iconSize='small'
                                        text={card.body}
                                        absFix={card.absfix}
                                        clickFn={card.link ? (() => handleNavigate(card.link!)) : card.fn} // closes menu if already on page
                                        key={key}
                                        hasArrow
                                    />
                                )
                            }

                            if (card.fn) {
                                return (
                                    <ActionCard
                                        icon={card.icon}
                                        iconSize='small'
                                        text={card.body}
                                        absFix={card.absfix}
                                        clickFn={card.fn}
                                        key={key}
                                        hasArrow
                                    />
                                )
                            }

                            return null
                        }
                    )
                )}

                <hr className='menu__break' />
                <ActionCard
                icon={<spriteIcons.IconLogOut />}
                iconSize='small'
                text={t('logOut')}
                clickFn={handleLogout}
                />

                {/* 
                <ActionCard
                icon={<spriteIcons.IconLogOut />}
                iconSize='small'
                text={t('logOut')}
                clickFn={() => {
                    modalData.addModal(
                    `${t('logOut')}?`,
                    <ConfirmModal
                        body='Are you sure you want to logout?'
                        confirmFn={handleLogout}
                    />
                    )
                }}
                />
                */}
            </WithHeaderSection>
            <DesktopAdditionalContent />
        </div>
    )
}
